// components/auth/Signup.js
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { signup, clearError, clearSignupState } from '../../slices/authSlice.mjs';
import { setHeaderData } from '../../slices/headerSlice.mjs';
import { getAuthorizedReturnUrl } from '../../utils/auth/returnUrlValidator.mjs';
import {
    setPageTitle,
    setPageDescription,
    resetPageMetadata,
} from '../../slices/pageMetadataSlice.mjs';

import useIsMounted from '../../hooks/useIsMounted.mjs';
import ThemePicker from '../common/ThemePicker.mjs';

// Component for client-side navigation functionality
// Only rendered on the client after hydration
const ClientSideSignupHandler = ({ isAuthenticated, user }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        if (isAuthenticated && user) {
            // Navigate to returnTo destination (validated) or home
            const returnTo = searchParams.get('returnTo');
            navigate(getAuthorizedReturnUrl(returnTo, user));
        }
    }, [isAuthenticated, user, navigate, searchParams]);

    return null; // This component doesn't render anything
};

/* ── Value proposition bullets for the hero panel ── */
const VALUE_PROPS = [
    { icon: '🏀', text: 'AI-powered sports analysis' },
    { icon: '🔍', text: 'Multi-source intelligence' },
    { icon: '💬', text: 'Conversational AI on every article' },
    { icon: '🎨', text: 'Personalized themes & layouts' },
];

/* ── Hero Panel (shared between success & form states) ── */
/* Defined at module scope so React preserves its identity across re-renders */
const HeroPanel = () => (
    <div className="signup-hero">
        {/* Animated orbs — same visual language as LandingPage */}
        <div className="signup-hero__orb signup-hero__orb--1" />
        <div className="signup-hero__orb signup-hero__orb--2" />
        <div className="signup-hero__orb signup-hero__orb--3" />

        <div className="signup-hero__content">
            <h2 className="signup-hero__heading">
                <span className="signup-hero__heading-line">Welcome to the</span>
                <span className="signup-hero__heading-accent">Nexus of Truth</span>
            </h2>

            <p className="signup-hero__sub">
                Your trusted source for AI-powered sports analysis, multi-source intelligence, and personalized coverage.
            </p>

            <ul className="signup-hero__props">
                {VALUE_PROPS.map((prop, i) => (
                    <li key={i} className="signup-hero__prop" style={{ '--prop-index': i }}>
                        <span className="signup-hero__prop-icon">{prop.icon}</span>
                        <span>{prop.text}</span>
                    </li>
                ))}
            </ul>
        </div>
    </div>
);

// Create a separate component for the signup content that will be wrapped by App
const SignupContent = () => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        screenName: '',
    });
    const [validationErrors, setValidationErrors] = useState({});
    const { isAuthenticated, loading, error, fieldErrors, signupSuccess, signupMessage, user } =
        useSelector((state) => state.auth || {});
    const dispatch = useDispatch();

    // Use the custom hook
    const isMounted = useIsMounted();

    // Add refs for fields that might need focus
    const emailRef = useRef(null);
    const screenNameRef = useRef(null);

    // Page metadata — run once on mount, clean up on unmount
    useEffect(() => {
        dispatch(setPageTitle('Sign Up - Nexus of Truth'));
        dispatch(setPageDescription('Create your Nexus of Truth account and start exploring AI-powered sports coverage.'));
        dispatch(
            setHeaderData({
                title: 'Sign Up',
                description: 'Join the Nexus of Truth.',
            })
        );

        return () => {
            dispatch(resetPageMetadata());
        };
    }, [dispatch]);

    // Clean up transient auth state on unmount
    useEffect(() => {
        return () => {
            dispatch(clearError());
            dispatch(clearSignupState());
        };
    }, [dispatch]);

    // Handle API errors when they appear in Redux state
    useEffect(() => {
        if (fieldErrors) {
            setValidationErrors((prev) => ({
                ...prev,
                ...fieldErrors,
            }));

            // Focus the first field with an error
            if (fieldErrors.email) emailRef.current?.focus();
            else if (fieldErrors.screenName) screenNameRef.current?.focus();
            // ... other fields
        } else if (error) {
            // Legacy error handling
            // Map common error messages to specific fields
            if (error.includes('Email already in use')) {
                setValidationErrors((prev) => ({
                    ...prev,
                    email: 'Email already in use',
                }));
                // Focus the email field
                emailRef.current?.focus();
            } else if (error.includes('Screen name already taken')) {
                setValidationErrors((prev) => ({
                    ...prev,
                    screenName: 'Screen name already taken',
                }));
                // Focus the screen name field
                screenNameRef.current?.focus();
            }
        }
    }, [error, fieldErrors]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });

        // Clear error when user starts typing
        if (validationErrors[name]) {
            setValidationErrors({
                ...validationErrors,
                [name]: '',
            });
        }

        // Real-time validation for screen name
        if (name === 'screenName') {
            const newErrors = { ...validationErrors };

            if (value && !/^[a-zA-Z0-9_-]+$/.test(value)) {
                newErrors.screenName = 'Only letters, numbers, underscores, and hyphens allowed';
            } else if (value && value.length < 3) {
                newErrors.screenName = 'Must be at least 3 characters';
            } else if (value && value.length > 30) {
                newErrors.screenName = 'Cannot exceed 30 characters';
            } else {
                delete newErrors.screenName;
            }

            setValidationErrors(newErrors);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }

        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }

        // Enhanced screen name validation (match server-side exactly)
        if (!formData.screenName.trim()) {
            newErrors.screenName = 'Screen name is required';
        } else if (formData.screenName.length < 3) {
            newErrors.screenName = 'Screen name must be at least 3 characters';
        } else if (formData.screenName.length > 30) {
            newErrors.screenName = 'Screen name cannot exceed 30 characters';
        } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.screenName)) {
            newErrors.screenName =
                'Screen name can only contain letters, numbers, underscores, and hyphens (no spaces)';
        }

        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        }

        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setValidationErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        // Just dispatch with the form data, no connection token
        dispatch(signup(formData));
    };

    // Show success message
    if (signupSuccess) {
        return (
            <div className="signup-split">
                <HeroPanel />
                <div className="signup-form-panel">
                    <div className="signup-card">
                        <div className="signup-success">
                            <div className="signup-success__icon">🎉</div>
                            <h1 className="signup-success__title">Account Created!</h1>
                            <p className="signup-success__message">{signupMessage}</p>
                            <p className="signup-success__note">
                                Please check your email and click the verification link to activate
                                your account.
                            </p>
                            <div className="signup-success__action">
                                {isMounted ? (
                                    <Link to={`/login${window.location.search}`} className="signup-cta signup-cta--primary">
                                        Go to Login →
                                    </Link>
                                ) : (
                                    <a href="/login" className="signup-cta signup-cta--primary">Go to Login →</a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {isMounted && <ClientSideSignupHandler isAuthenticated={isAuthenticated} user={user} />}

            <div className="signup-split">
                <HeroPanel />

                <div className="signup-form-panel">
                    <div className="signup-card">
                        <h1 className="signup-card__title">Create Account</h1>

                        {error && (
                            <div className="signup-card__error">
                                {typeof error === 'object'
                                    ? error.message || JSON.stringify(error)
                                    : error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="signup-form">
                            {/* Name row — side by side */}
                            <div className="signup-form__row">
                                <div className="signup-field">
                                    <label htmlFor="firstName">First Name</label>
                                    <input
                                        type="text"
                                        id="firstName"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        className={validationErrors.firstName ? 'error' : ''}
                                        placeholder="Jane"
                                    />
                                    {validationErrors.firstName && (
                                        <div className="signup-field__error">{validationErrors.firstName}</div>
                                    )}
                                </div>

                                <div className="signup-field">
                                    <label htmlFor="lastName">Last Name</label>
                                    <input
                                        type="text"
                                        id="lastName"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        className={validationErrors.lastName ? 'error' : ''}
                                        placeholder="Doe"
                                    />
                                    {validationErrors.lastName && (
                                        <div className="signup-field__error">{validationErrors.lastName}</div>
                                    )}
                                </div>
                            </div>

                            <div className="signup-field">
                                <label htmlFor="screenName">Screen Name</label>
                                <input
                                    ref={screenNameRef}
                                    type="text"
                                    id="screenName"
                                    name="screenName"
                                    value={formData.screenName}
                                    onChange={handleChange}
                                    className={validationErrors.screenName ? 'error' : ''}
                                    placeholder="e.g., jane_doe, user123"
                                />
                                {validationErrors.screenName ? (
                                    <div className="signup-field__error">{validationErrors.screenName}</div>
                                ) : (
                                    <div className="signup-field__hint">
                                        3-30 characters. Letters, numbers, underscores, and hyphens only.
                                    </div>
                                )}
                            </div>

                            <div className="signup-field">
                                <label htmlFor="email">Email</label>
                                <input
                                    ref={emailRef}
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={validationErrors.email ? 'error' : ''}
                                    placeholder="you@example.com"
                                />
                                {validationErrors.email && (
                                    <div className="signup-field__error">{validationErrors.email}</div>
                                )}
                            </div>

                            {/* Password row — side by side */}
                            <div className="signup-form__row">
                                <div className="signup-field">
                                    <label htmlFor="password">Password</label>
                                    <input
                                        type="password"
                                        id="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        className={validationErrors.password ? 'error' : ''}
                                        placeholder="Min. 8 characters"
                                    />
                                    {validationErrors.password && (
                                        <div className="signup-field__error">{validationErrors.password}</div>
                                    )}
                                </div>

                                <div className="signup-field">
                                    <label htmlFor="confirmPassword">Confirm Password</label>
                                    <input
                                        type="password"
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        className={validationErrors.confirmPassword ? 'error' : ''}
                                        placeholder="Re-enter password"
                                    />
                                    {validationErrors.confirmPassword && (
                                        <div className="signup-field__error">{validationErrors.confirmPassword}</div>
                                    )}
                                </div>
                            </div>

                            {/* Theme selection — compact, integrated */}
                            <div className="signup-theme-section">
                                <p className="signup-theme-section__label">Choose Your Theme</p>
                                <ThemePicker className="signup-theme-picker" />
                            </div>

                            <button type="submit" className="signup-cta signup-cta--submit" disabled={loading}>
                                {loading ? 'Creating Account...' : 'Create Account →'}
                            </button>
                        </form>

                        <div className="signup-card__footer">
                            {isMounted ? (
                                <Link to={`/login${window.location.search}`}>Already have an account? <strong>Log In</strong></Link>
                            ) : (
                                <a href="/login">Already have an account? <strong>Log In</strong></a>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

// Export the pure content; <App> is mounted by hydrate.js / SSR controllers
export default SignupContent;
